import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { fetchUserPreferences, saveUserPreference } from '../lib/userPreferences';
import { accountGeneration, accountStorage, cachedUserIdOwner, scopeToAccount } from '../lib/accountStorage';
import { Faith, FAITH_VALUES, DEFAULT_FAITH } from '../constants/faith';

type StartDay = 'sunday' | 'monday' | 'saturday';
const DEFAULT_START_DAY: StartDay = 'sunday';
const VALID_START_DAYS: StartDay[] = ['sunday', 'monday', 'saturday'];

interface SettingsState {
  startDay: StartDay;
  /** User faith (Existential Faith workstream) — gates the Islamic layer. */
  faith: Faith;
  initializedUserId: string | null;
  cachedUserId: string | null;
  initialize: () => Promise<void>;
  setStartDay: (day: StartDay) => void;
  setFaith: (faith: Faith) => void;
}

export const useSettingsStore = create<SettingsState>()(
  persist<SettingsState, [], [], any>(
    (set, get) => ({
      startDay: DEFAULT_START_DAY,
      faith: DEFAULT_FAITH,
      initializedUserId: null,
      cachedUserId: null,

      // Load-on-login from the shared user_preferences row (start_day + faith).
      initialize: async () => {
        const at = accountGeneration();
        const res = await fetchUserPreferences();
        // An answer for an account that has since signed out is not this copy's.
        if (at !== accountGeneration()) return;
        if (!res) return; // logged out
        if (get().initializedUserId === res.userId) return;
        set((state) => {
          const cacheIsOurs =
            state.cachedUserId === null || state.cachedUserId === res.userId;
          const cloud = res.row?.start_day;
          const startDay: StartDay = VALID_START_DAYS.includes(cloud as StartDay)
            ? (cloud as StartDay)
            : cacheIsOurs
            ? state.startDay
            : DEFAULT_START_DAY;
          // Faith: cloud value wins; else keep our cache; else default to Muslim
          // (so existing users / unset faith see the Islamic layer — opt-out).
          const cloudFaith = res.row?.faith;
          const faith: Faith = FAITH_VALUES.includes(cloudFaith as Faith)
            ? (cloudFaith as Faith)
            : cacheIsOurs
            ? state.faith
            : DEFAULT_FAITH;
          return { startDay, faith, initializedUserId: res.userId, cachedUserId: res.userId };
        });
      },

      setStartDay: (day) => {
        set({ startDay: day });
        void saveUserPreference({ start_day: day });
      },

      setFaith: (faith) => {
        set({ faith });
        void saveUserPreference({ faith });
      },
    }),
    {
      name: 'settings-storage',
      // ⭐ One saved copy per account — see `scopeToAccount` below.
      storage: accountStorage(cachedUserIdOwner),
      version: 1,
      partialize: (state: SettingsState) => ({
        startDay: state.startDay,
        faith: state.faith,
        cachedUserId: state.cachedUserId,
      }),
      migrate: (persistedState: any, version: number) => {
        if (version === 0) {
          // Migration from version 0: Set default start day if needed
          return { startDay: persistedState.startDay || 'sunday' } as unknown as SettingsState;
        }
        return persistedState as SettingsState;
      }
    }
  )
);

/* ⚠️⚠️ ONE SAVED COPY PER ACCOUNT (lib/accountStorage). The device kept one copy, tagged with the
   account it was loaded for, and a sign-out does not reload the page: the next account got the last
   one's week start and FAITH — which gates the Islamic layer — until its own load (and kept them when
   its row had none); a choice whose write never landed was replaced by the next account's load. */
scopeToAccount(useSettingsStore);
